import Phaser from "phaser";
import { EFFECT_TEXTURES } from "../assets";
import type { Element } from "../model/board";
import { COLORS } from "./palette";
import { drawDiamond } from "./BattlefieldView";
import { BOARD_LAYOUT } from "./iso";
import { stageX, stageY } from "./stage";

type Point = { x: number; y: number };
const bandFor = (tier: number) => tier < 3 ? 0 : tier < 5 ? 1 : tier < 6 ? 2 : 3;

export class Effects {
  private sparks = new Map<Element, Phaser.GameObjects.Particles.ParticleEmitter>();
  private motes = new Map<Element, Phaser.GameObjects.Particles.ParticleEmitter>();
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  constructor(private scene: Phaser.Scene, private motion: boolean) {
    this.makeTextures();
    if (!motion) return;
    for (const element of ["fire", "ice", "lightning"] as const) {
      const key = `fx-${element}`;
      const spark = scene.add.particles(0, 0, key, { emitting: false,
        lifespan: { min: 220, max: 530 }, speed: { min: 25, max: 100 }, angle: { min: 0, max: 360 },
        alpha: { start: 0.85, end: 0 }, scale: { start: 0.4, end: 0.05 },
        rotate: { min: -45, max: 45 }, gravityY: element === "fire" ? -65 : 45,
        maxAliveParticles: 110 }).setDepth(550);
      const mote = scene.add.particles(0, 0, key, { emitting: false,
        lifespan: 620, speed: { min: 6, max: 20 }, angle: { min: 225, max: 315 },
        alpha: { start: 0.6, end: 0 }, scale: { start: 0.25, end: 0.03 },
        maxAliveParticles: 80 }).setDepth(510);
      this.sparks.set(element, spark); this.motes.set(element, mote); this.emitters.push(spark, mote);
    }
  }
  setSpeed(speed: number): void { this.emitters.forEach(emitter => { emitter.timeScale = speed; }); }
  private makeTextures(): void {
    for (const key of ["fire", "ice", "lightning", "brush", "glow"]) {
      if (this.scene.textures.exists(`fx-${key}`)) continue;
      const texture = this.scene.textures.createCanvas(`fx-${key}`, 48, 48)!;
      const ctx = texture.getContext();
      if (key === "glow") {
        const glow = ctx.createRadialGradient(24, 24, 0, 24, 24, 24);
        glow.addColorStop(0, "rgba(255,250,233,0.65)"); glow.addColorStop(0.22, "rgba(229,209,255,0.3)");
        glow.addColorStop(1, "rgba(216,195,255,0)"); ctx.fillStyle = glow; ctx.fillRect(0, 0, 48, 48);
      } else {
        ctx.fillStyle = key === "fire" ? "#ffe299" : key === "ice" ? "#a9e8f6" : key === "lightning" ? "#fff6d5" : "#fff4d4";
        const points = key === "fire" ? [[18,35],[23,9],[30,35],[24,30]] : key === "ice" ? [[15,29],[25,10],[33,27],[23,37]] :
          key === "lightning" ? [[19,8],[31,8],[25,22],[34,22],[17,39],[22,27],[14,27]] : [[11,17],[35,13],[37,29],[14,34]];
        ctx.beginPath(); points.forEach(([x, y], i) => { if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); }); ctx.closePath(); ctx.fill();
        // Broken translucent strokes give chips a brushed surface at tiny scale.
        ctx.strokeStyle = "rgba(255,255,240,0.55)"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(21,17); ctx.lineTo(27,26); ctx.stroke();
        ctx.strokeStyle = "rgba(128,105,81,0.16)"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(18,29); ctx.lineTo(27,31); ctx.stroke();
      }
      texture.refresh();
    }
  }
  idle(element: Element, point: Point): void { this.motes.get(element)?.emitParticleAt(point.x, point.y, 1); }
  area(element: Element, tiles: Point[], tier: number): void {
    const g = this.scene.add.graphics().setDepth(4);
    g.fillStyle(COLORS[element].body, 0.2).lineStyle(1.5, COLORS[element].light, 0.5);
    for (const p of tiles) {
      drawDiamond(g, p.x, p.y, BOARD_LAYOUT.tileWidth - 2, BOARD_LAYOUT.tileHeight - 2); g.fillPath().strokePath();
      if (this.motion) this.particle(element, p.x, p.y - 12);
    }
    this.scene.tweens.add({ targets: g, alpha: 0, duration: this.motion ? 560 : 200, onComplete: () => g.destroy() });
    if (this.motion && tier >= 6) this.scene.cameras.main.shake(85, 0.0015);
  }
  particle(element: Element, x: number, y: number, burst = false, strength = 1): void {
    this.sparks.get(element)?.emitParticleAt(x, y, burst ? Math.round(5 * strength) : 1);
  }
  burst(element: Element, x: number, y: number, strength = 1): void {
    const flash = this.scene.add.star(x, y, 6, 4, 15 * strength, COLORS[element].light, 0.75).setDepth(540);
    this.scene.tweens.add({ targets: flash, scale: this.motion ? 1.6 : 1, alpha: 0,
      duration: this.motion ? 230 : 120, onComplete: () => flash.destroy() });
    this.particle(element, x, y, true, strength);
    if (element === "fire" && this.motion) {
      const flame = this.scene.add.image(x, y + 6, EFFECT_TEXTURES.fire).setOrigin(0.88, 0.5)
        .setDisplaySize(63 * strength, 31 * strength).setRotation(-Math.PI / 2).setDepth(535).setAlpha(0.8);
      this.scene.tweens.add({ targets: flame, y: y - 13, alpha: 0, scaleX: flame.scaleX * 1.35,
        scaleY: flame.scaleY * 1.3, duration: 260, ease: "Cubic.Out", onComplete: () => flame.destroy() });
    }
  }
  mergeBurst(element: Element, x: number, y: number, strength: number): void {
    this.burst(element, x, y - 38, strength);
    const ring = this.scene.add.ellipse(x, y + 2, 35, 15).setStrokeStyle(3, COLORS[element].light, 0.8).setDepth(5);
    this.scene.tweens.add({ targets: ring, scale: this.motion ? 3.1 : 1, alpha: 0,
      duration: 420, ease: "Cubic.Out", onComplete: () => ring.destroy() });
    if (this.motion) this.scene.cameras.main.shake(80, 0.0012);
  }
  victory(x: number, y: number, breakthrough = false): void {
    if (!this.motion) return;
    const origins = breakthrough ? [90, 370, 768, 1166, 1446].map(stageX) : [x - 110, x + 110];
    for (const origin of origins) {
      const offset = origin - x;
      const confetti = this.scene.add.particles(origin, breakthrough ? stageY(955) : y + 55, "fx-brush", {
        emitting: false, lifespan: { min: breakthrough ? 2600 : 1400, max: breakthrough ? 4200 : 2100 },
        angle: { min: breakthrough ? 245 : offset < 0 ? 235 : 195, max: breakthrough ? 295 : offset < 0 ? 300 : 255 },
        speed: { min: breakthrough ? 440 : 130, max: breakthrough ? 770 : 330 }, gravityY: breakthrough ? 190 : 300,
        scale: { start: 0.42, end: 0.16 }, alpha: { start: 1, end: 0 },
        rotate: { min: -180, max: 180 }, tint: [0xf6ca78, 0xec967a, 0xa6d5af, 0x8bcde2, 0xc4a5df],
        maxAliveParticles: breakthrough ? 110 : 80 }).setDepth(670);
      this.emitters.push(confetti); confetti.explode(breakthrough ? 60 : 38);
      if (breakthrough) this.scene.time.delayedCall(650, () => confetti.explode(36));
      this.scene.time.delayedCall(breakthrough ? 4800 : 2300, () => {
        this.emitters = this.emitters.filter(e => e !== confetti); confetti.destroy();
      });
    }
    const glow = this.scene.add.image(x, y, "fx-glow").setDisplaySize(310, 310).setDepth(599).setAlpha(0.6);
    this.scene.tweens.add({ targets: glow, alpha: 0, scaleX: glow.scaleX * 1.6, scaleY: glow.scaleY * 1.6,
      duration: 1700, ease: "Cubic.Out", onComplete: () => glow.destroy() });
  }
  private trail(element: Element, point: Point, angle: number, band: number): void {
    if (!this.motion) return;
    this.particle(element, point.x, point.y);
    if (element !== "fire" || !band) return;
    const wisp = this.scene.add.image(point.x, point.y, EFFECT_TEXTURES.fire).setOrigin(0.88, 0.5)
      .setDisplaySize(37 + band * 13, 18 + band * 6).setRotation(angle).setDepth(518).setAlpha(0.3);
    this.scene.tweens.add({ targets: wisp, y: point.y - 8, alpha: 0, scaleX: wisp.scaleX * 0.35,
      scaleY: wisp.scaleY * 0.35, duration: 210, ease: "Cubic.Out", onComplete: () => wisp.destroy() });
  }
  projectile(element: Element, from: Point, to: Point, tier: number, duration: number, impact: () => void = () => {}): void {
    const band = bandFor(tier), strength = [0.55, 1.1, 1.9, 2.6][band] * (1 + Math.min(tier - 1, 10) * 0.035);
    const charge = this.scene.add.image(from.x, from.y, "fx-glow").setDisplaySize(22 + band * 17, 22 + band * 17).setDepth(525);
    this.scene.tweens.add({ targets: charge, scaleX: charge.scaleX * 1.7, scaleY: charge.scaleY * 1.7, alpha: 0,
      duration: 85, onComplete: () => charge.destroy() });
    const anticipation = 75, travelTime = Math.max(1, duration - anticipation);
    if (element === "lightning" && band > 0) { this.lightning(from, to, band, strength, anticipation, travelTime, impact); return; }
    const missile = this.missile(element, from, band);
    const dx = to.x - from.x, dy = to.y - from.y;
    const arc = this.motion ? Math.min(45, Math.hypot(dx, dy) * 0.07) : 0;
    missile.setDepth(520).setAlpha(0);
    let lastTrail = 0;
    this.scene.tweens.addCounter({ from: 0, to: 1, delay: anticipation, duration: travelTime, ease: "Sine.In",
      onUpdate: tween => {
        const t = tween.getValue()!;
        const x = from.x + dx * t, y = from.y + dy * t - Math.sin(t * Math.PI) * arc;
        const tangent = Math.atan2(dy - Math.cos(t * Math.PI) * arc * Math.PI, dx);
        missile.setPosition(x, y).setRotation(tangent).setAlpha(1);
        if (element === "fire" && this.motion) missile.setScale(1 + Math.sin(t * 35) * 0.05, 1 + Math.cos(t * 31) * 0.07);
        if (tween.elapsed - lastTrail > 28) { lastTrail = tween.elapsed; this.trail(element, { x, y }, tangent, band); }
      }, onComplete: () => { this.burst(element, to.x, to.y, strength); impact(); missile.destroy(); } });
  }
  private missile(element: Element, from: Point, band: number): Phaser.GameObjects.Container {
    const root = this.scene.add.container(from.x, from.y);
    if (element === "fire") {
      const size = [52, 108, 155, 198][band];
      if (band >= 2) for (const sign of (band === 3 ? [-2,-1,1,2] : [-1, 1])) root.add(this.scene.add.image(-24, sign * 18, EFFECT_TEXTURES.fire)
        .setOrigin(0.88, 0.5).setDisplaySize(91, 42).setAngle(sign * 7).setAlpha(0.85));
      root.add(this.scene.add.image(0, 0, EFFECT_TEXTURES.fire).setOrigin(0.88, 0.5).setDisplaySize(size, size / 2));
    } else if (element === "ice") {
      const size = [0.5, 1.15, 1.75, 2.15][band];
      if (band) for (let i = 0; i < (band >= 2 ? band * 2 : 2); i++) {
        const sign = i % 2 ? 1 : -1;
        root.add(this.iceShard().setPosition(-13 - Math.floor(i / 2) * 17, sign * (11 + Math.floor(i / 2) * 10))
          .setScale(size * 0.55).setAngle(sign * 11));
      }
      root.add(this.iceShard().setScale(size));
    } else {
      const spark = this.scene.add.graphics().lineStyle(3, 0xd9b6ed).lineBetween(-12, 6, -2, -5).lineBetween(-2, -5, 7, 5).lineBetween(7, 5, 14, -6);
      spark.lineStyle(1.3, 0xfffef0).lineBetween(-12, 6, -2, -5).lineBetween(-2, -5, 7, 5).lineBetween(7, 5, 14, -6);
      root.add(spark);
    }
    return root;
  }
  private lightning(from: Point, to: Point, band: number, strength: number, delay: number, duration: number, impact: () => void): void {
    const bolt = this.scene.add.graphics().setDepth(520).setAlpha(0);
    const dx = to.x - from.x, dy = to.y - from.y, distance = Math.hypot(dx, dy) || 1;
    const normal = { x: -dy / distance, y: dx / distance };
    const path = (a: Point, b: Point, noise: number) => {
      const points = [new Phaser.Math.Vector2(a.x, a.y)];
      for (let i = 1; i < 9; i++) {
        const t = i / 9, offset = (i % 2 ? 1 : -1) * (noise * (0.55 + Math.random() * 0.45));
        points.push(new Phaser.Math.Vector2(a.x + (b.x - a.x) * t + normal.x * offset, a.y + (b.y - a.y) * t + normal.y * offset));
      }
      points.push(new Phaser.Math.Vector2(b.x, b.y)); return points;
    };
    const paint = (points: Phaser.Math.Vector2[], power: number) => {
      // Several low-opacity brush-width layers make a soft fringe around white ink.
      for (const [width, alpha] of [[39, 0.025], [28, 0.05], [18, 0.1]] as const)
        bolt.lineStyle(width * power, 0xc3a4e6, alpha).strokePoints(points);
      bolt.lineStyle(8 * power, 0xb9a0df, 0.6).strokePoints(points);
      bolt.lineStyle(4 * power, 0xffeeb5, 0.85).strokePoints(points);
      bolt.lineStyle(1.7 * power, 0xffffff, 1).strokePoints(points);
    };
    let lastRedraw = -1;
    this.scene.tweens.addCounter({ from: 0, to: 1, delay, duration,
      onUpdate: tween => {
        const frame = Math.floor(tween.elapsed / 65);
        if (frame === lastRedraw || (!this.motion && lastRedraw >= 0)) return;
        lastRedraw = frame; bolt.clear();
        const progress = tween.getValue()!;
        const head = { x: from.x + dx * progress, y: from.y + dy * progress };
        const main = path(from, head, band >= 2 ? 21 : 12); paint(main, band === 3 ? 2.2 : band === 2 ? 1.6 : 1);
        if (band >= 2) {
          const fork = main[5];
          for (const sign of (band === 3 ? [-2,-1,1,2] : [-1, 1])) {
            const end = { x: head.x + normal.x * sign * 40, y: head.y + normal.y * sign * 40 };
            paint(path(fork, end, 13), 1.1);
            if (this.motion) this.sparks.get("lightning")?.emitParticleAt(end.x, end.y, 2);
          }
          if (this.motion) for (const index of [2, 4, 6]) {
            const p = main[index], sign = index === 4 ? -1 : 1;
            const a = { x: p.x + normal.x * sign * 19, y: p.y + normal.y * sign * 19 };
            const b = { x: a.x + dx / distance * 15, y: a.y + dy / distance * 15 };
            paint(path(a, b, 3), 0.4);
          }
        }
        bolt.setAlpha(this.motion ? 1 - tween.getValue()! * 0.25 : 0.45);
      }, onComplete: () => { bolt.destroy(); this.burst("lightning", to.x, to.y, strength); impact(); } });
  }
  private iceShard(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const points = [new Phaser.Math.Vector2(-18, -7), new Phaser.Math.Vector2(-4, -11), new Phaser.Math.Vector2(17, 0),
      new Phaser.Math.Vector2(-4, 11), new Phaser.Math.Vector2(-18, 7)];
    g.fillStyle(0x80c9e5).fillPoints(points, true);
    g.fillStyle(0xd8faff).fillTriangle(-16, -5, 17, 0, -3, -10);
    g.fillStyle(0xeefeff).fillTriangle(-14, 2, 15, 0, -2, 7);
    g.lineStyle(1, 0x388cac, 0.7).strokePoints(points, true); return g;
  }
}
